import React, { useCallback } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAuthState } from "@web-src/features/auth/authSlice";
import { PageRoute } from "@web-src/features/app/types";
import {
  SubscriptionPlanModuleId,
  useRestApiProvider,
  UserRole,
} from "@jugl-web/rest-api";
import { EntityGateway } from "@web-src/modules/entities/components/EntityGateway";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { FullPageError } from "@web-src/modules/common/components/FullPageError";
import { EmptyListContent, PageLoaderFull } from "@jugl-web/ui-components";
import { usePrevious } from "@web-src/utils/helper";
import { AnonymousSessionGateway } from "@web-src/modules/auth/components/AnonymousSessionGateway";
import { AnonymousSessionProvider } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { getLoginFlow } from "@jugl-web/utils/hooks/useLoginFlow";
import { useMe } from "../../hooks/useMe";
import { useNavigateToRoleBasedInitialPage } from "../../hooks/useNavigateToRoleBasedInitialPage";
import { NavigateToDefaultEntity } from "../NavigateToDefaultEntity/NavigateToDefaultEntity";

const PrivateRoute: React.FC<{
  component: React.ReactNode;
  profileRequired?: boolean;
  entityRequired?: boolean;
  activeSubscriptionRequired?: boolean;
  anonymousAllowed?: boolean;
  requiredUserRoles?: UserRole[];
  requiredCXUserRoles?: UserRole[];
  requiredModules?: SubscriptionPlanModuleId[];
}> = ({
  component,
  profileRequired = true,
  entityRequired = true,
  activeSubscriptionRequired = true,
  anonymousAllowed = false,
  requiredUserRoles,
  requiredModules,
  requiredCXUserRoles,
}) => {
  const { isAuthenticated } = useSelector(selectAuthState);

  const previousIsAuthenticated = usePrevious(isAuthenticated);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const linkToOpenAfterLogin = searchParams.get("next");
  const {
    isInitialized: isEntityInitialized,
    entity,
    isEntitiesFetching,
    reinitialize,
    isInitializationError,
    subscriptionInfo,
    isModuleAvailable,
  } = useEntityProvider();
  const { usersApi } = useRestApiProvider();

  const { me, isLoading: isMeLoading } = useMe();
  const {
    data: profileData,
    error: profileError,
    isLoading: isProfileLoading,
    isError: isProfileLoadingError,
    refetch: refetchProfileData,
  } = usersApi.useGetUserProfileQuery(
    {},
    {
      skip: !isAuthenticated,
      refetchOnMountOrArgChange: false,
    }
  );
  const hasProfile = !!profileData;

  const handleRetryOnError = useCallback(async () => {
    if (isInitializationError) {
      reinitialize();
    }
    if (isProfileLoadingError) {
      refetchProfileData();
    }
  }, [
    isInitializationError,
    isProfileLoadingError,
    refetchProfileData,
    reinitialize,
  ]);

  const navigateToRoleBasedInitialPage = useNavigateToRoleBasedInitialPage();
  const isInvitationsPage = location.pathname === "/invitations";

  if (!isAuthenticated && anonymousAllowed) {
    return (
      <AnonymousSessionProvider>
        <AnonymousSessionGateway>{component}</AnonymousSessionGateway>
      </AnonymousSessionProvider>
    );
  }
  if (
    (isEntitiesFetching && !isInvitationsPage) ||
    isAuthenticated === undefined
  ) {
    return <PageLoaderFull />;
  }
  if (
    (profileRequired &&
      isProfileLoadingError &&
      (profileError as unknown as { status?: number }).status !== 404) ||
    isInitializationError
  ) {
    return <FullPageError onRetry={handleRetryOnError} />;
  }
  if (previousIsAuthenticated === undefined && isAuthenticated === false) {
    const requestedPath = `${location.pathname}${location.search}`;
    let next = "";
    if (linkToOpenAfterLogin !== requestedPath) {
      next = requestedPath;
    }
    return (
      <Navigate
        to={{
          pathname: `/${PageRoute.login}`,
          search: next ? `?next=${next}` : "",
        }}
      />
    );
  }

  if (
    !isAuthenticated ||
    (profileRequired && isProfileLoading && !hasProfile) ||
    (!me && isMeLoading) ||
    (entityRequired && !isEntityInitialized) ||
    (entityRequired && !entity && isEntitiesFetching)
  ) {
    return <PageLoaderFull />;
  }

  if (linkToOpenAfterLogin) {
    return <Navigate to={linkToOpenAfterLogin} />;
  }

  if (isAuthenticated && !hasProfile && profileRequired) {
    if (getLoginFlow() === "cx") {
      return <Navigate to="/cx/onboarding" />;
    }
    return (
      <Navigate
        to={{
          pathname: `/${PageRoute.profileForm}`,
        }}
      />
    );
  }

  if (isAuthenticated && entityRequired) {
    if (!entity) {
      return <NavigateToDefaultEntity />;
    }
    const hasActiveSubscription =
      !!subscriptionInfo && subscriptionInfo.planIsActive;
    if (activeSubscriptionRequired && !hasActiveSubscription) {
      return (
        <Navigate
          to={{ pathname: `/${entity.id}/${PageRoute.noSubscription}` }}
        />
      );
    }
    if (entity.type === "cx" && requiredCXUserRoles) {
      if (!requiredCXUserRoles.includes(entity.role)) {
        navigateToRoleBasedInitialPage(entity);
        return null;
      }
    } else if (requiredUserRoles && !requiredUserRoles.includes(entity.role)) {
      navigateToRoleBasedInitialPage(entity);
      return null;
    }
    if (
      requiredModules &&
      !requiredModules.every((module) => isModuleAvailable(module))
    ) {
      return (
        <EmptyListContent
          type="error"
          customTitle="Page not found"
          customSubtitle="Please try something else"
          customButton={{
            text: "Go home",
            onClick: () => navigateToRoleBasedInitialPage(entity),
          }}
        />
      );
    }
    return <EntityGateway>{component}</EntityGateway>;
  }
  return <>{component}</>;
};

export default PrivateRoute;
